import { coerceNumberProperty } from '@angular/cdk/coercion';
import {
    ChangeDetectorRef,
    Directive,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
    ViewRef
} from '@angular/core';
import { Meta } from '@df/core/meta/meta';
import { Status } from '@df/core/status';
import { Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

/**
 * Qty Select Component.
 * Use for creating new quantity select components with the same logic and
 * custom template.
 */
@Directive()
// eslint-disable-next-line @angular-eslint/directive-class-suffix
export class QtySelectComponentDef implements OnChanges, OnInit, OnDestroy {
    /**
     * Min qty
     * @type {number}
     */
    @Input()
    min?: number;

    /**
     * Max qty
     * @type {number}
     */
    @Input()
    max?: number;

    /**
     * Status setter
     * Status is required
     */
    protected _status?: Status;

    @Input()
    set status(status: Status) {
        this._status = status;

        if (this.subscription) {
            this.subscription.unsubscribe();
            this.subscription = undefined;
        }

        if (status) {
            this.subscription = status.subscribe(() => this.onStatusChange());
        }
    }
    get status(): Status | undefined {
        return this._status;
    }

    /**
     * Is disabled
     * @type {boolean}
     */
    @Input()
    isDisabled?: boolean;

    @Input()
    debounce = 100;

    @Input()
    custom: any;

    /**
     * Model values setter
     * @param {number | string | null} value
     */
    protected _model?: number;

    @Input()
    set model(value: number | string | null) {
        if (this.status?.busy) {
            return;
        }

        if (typeof value === 'number') {
            if (typeof this.min !== 'undefined' && value < this.min) {
                value = this.min;
            }

            if (this.max !== undefined && value > this.max) {
                value = this.max;
            }

            if (value !== this.model) {
                this._model = value;
                this.emitChange();
            }
        } else if (this.inputElement?.nativeElement) {
            this._model = undefined;
        }
        this.setInputValue();
    }

    protected setInputValue() {
        if (this.inputElement) {
            this.inputElement.nativeElement.value = this.model?.toString() || '';
        }
    }

    get model(): number | string | undefined {
        return this._model;
    }

    @Output()
    modelChange: EventEmitter<number | string> = new EventEmitter<number | string>();

    /**
     * On change function to call
     *
     * Typical usage will be either
     * (qtyChange)="setQty($event)"
     *
     * @type {function}
     */
    @Output()
    qtyChange: EventEmitter<number | string> = new EventEmitter<number | string>();

    @ViewChild('input')
    inputElement?: ElementRef<HTMLInputElement>;

    /**
     * Status subscription
     * @type {[type]}
     */
    protected subscription?: Subscription;
    protected subscriptions = new Subscription();

    /**
     * Class constructor
     * @param {ElementRef} protected ngEl
     * @param {Renderer}   protected renderer
     */
    public constructor(
        private ref: ChangeDetectorRef,
        readonly meta: Meta
    ) {}

    ngOnInit(): void {
        const debounce = coerceNumberProperty(this.debounce, 100);

        this.subscriptions.add(
            this.modelChange
                .pipe(
                    debounceTime(debounce), // wait ms after the last event before emitting last event
                    distinctUntilChanged() // only emit if value is different from previous value
                )
                .subscribe(model => this.qtyChange.emit(model))
        );
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['min']) {
            if (typeof this.min !== 'number') {
                this.min = 1;
            }

            if (!this.model || Number(this.model) < this.min) {
                this.model = this.min;
            }
        }
    }

    ngOnDestroy(): void {
        this.subscriptions?.unsubscribe();
    }

    /**
     * Increases qty
     * @param  {int} qty default 1
     */
    more(): void {
        if (this.canMore()) {
            (this.model as number)++;
            this.detectChanges();
        }
    }

    /**
     * Decreases qty
     * @param {int} qty default 1
     */
    less(): void {
        if (this.canLess()) {
            (this.model as number)--;
            this.detectChanges();
        }
    }

    protected getValueForIncrementCheck() {
        return this.model;
    }

    canLess(): boolean {
        const value = this.getValueForIncrementCheck();
        if (this.isDisabled || typeof value !== 'number') {
            return false;
        }

        if (typeof this.min === 'number' && this.min >= value) {
            return false;
        }

        return true;
    }

    canMore(): boolean {
        const value = this.getValueForIncrementCheck();

        if (this.isDisabled || typeof value !== 'number') {
            return false;
        }

        if (typeof this.max === 'number' && this.max <= value) {
            return false;
        }

        return true;
    }

    validate() {
        if (this.model === undefined) {
            this.model = this.min !== undefined ? this.min : 0;
        }
    }

    /**
     * If component onChange is set then notify
     */
    emitChange(): void {
        if (this.model !== undefined) {
            this.modelChange.next(this.model);
        }
    }

    onStatusChange() {
        this.validate();

        this.detectChanges();
    }

    detectChanges(): void {
        if (!(<ViewRef>this.ref).destroyed) {
            this.ref.detectChanges();
        }
    }
}
